@let financialAccount = financialAccountDetails$ | async;
@let isLoading = !financialAccount;

<div class="entity-details-container">
  <div class="entity-details-header">
    <div class="entity-details-title">Financial Account Details</div>
    @if (isIntegrated && !unmaskedAccountNumber) {
      <button
        rktButton
        btnType="link"
        (click)="loadFinancialAccountDetails()"
        title="Reveal Financial Account Number"
        [disabled]="!!(isLoading | accessControl | async)"
        class="reveal-btn"
      >
        Reveal
      </button>
    }
    @if (isIntegrated && unmaskedAccountNumber) {
      <button
        rktButton
        btnType="link"
        (click)="hideFinancialAccountDetails()"
        title="Hide Financial Account Number"
        [disabled]="!!(isLoading | accessControl | async)"
        class="reveal-btn"
      >
        Hide
      </button>
    }
  </div>

  @if (isEscrowAccount) {
    <div class="details-row details-row-with-offset">
      <app-details-item
        class="fa-details-balance"
        [showPlaceholder]="isLoading"
        label="Account Balance"
        [value]="
          financialAccount?.embeddedAccountBalance?.[financialAccount?.currency ?? 'USD']?.availableBalance ?? '0'
            | amount: financialAccount?.currency ?? 'USD'
        "
      ></app-details-item>
      <app-details-item
        class="fa-details-available-balance"
        [showPlaceholder]="isLoading"
        label="Available Balance"
        [value]="
          financialAccount?.embeddedAccountBalance?.[financialAccount?.currency ?? 'USD']?.accountBalance ?? '0'
            | amount: financialAccount?.currency ?? 'USD'
        "
      ></app-details-item>
    </div>
  }
  @if (isEscrowAccount) {
    <div class="details-row details-row-with-offset">
      <app-details-item
        class="fa-details-pending-deposits"
        [showPlaceholder]="isLoading"
        label="Pending Deposits"
        [value]="
          financialAccount?.embeddedAccountBalance?.[financialAccount?.currency ?? 'USD']?.pendingDeposit ?? '0'
            | amount: financialAccount?.currency ?? 'USD'
        "
      ></app-details-item>
      @if (financialAccount?.bankAccount) {
        <app-details-item
          class="fa-details-pending-withdrawals"
          [showPlaceholder]="isLoading"
          label="Pending Withdrawals"
          [value]="
            financialAccount?.embeddedAccountBalance?.[financialAccount?.currency ?? 'USD']?.pendingWithdraw ?? '0'
              | amount: financialAccount?.currency ?? 'USD'
          "
        ></app-details-item>
      }
    </div>
  }

  @if (isEscrowAccount) {
    <div class="details-separator"></div>
  }

  <div class="details-row details-row-with-offset">
    @if (entityType !== 'business-account') {
      <app-details-item
        class="fa-details-name"
        [showPlaceholder]="isLoading"
        label="Financial Account Name"
        [title]="$safeNavigationMigration(financialAccount?.name)"
        [value]="$safeNavigationMigration(financialAccount?.name)"
      ></app-details-item>
    }

    <app-details-item
      class="fa-details-id"
      [showPlaceholder]="isLoading"
      label="Financial Account ID"
      [title]="$safeNavigationMigration(financialAccount?.id) | uppercase"
      [value]="$safeNavigationMigration(financialAccount?.financialAccountShortId)"
    ></app-details-item>

    <app-details-item
      class="fa-details-account-category"
      [showPlaceholder]="isLoading"
      label="Account Category"
      [value]="$safeNavigationMigration(financialAccount?.category)"
    ></app-details-item>

    @if (financialAccount?.currency) {
      <app-details-item
        class="fa-details-currency"
        [showPlaceholder]="isLoading"
        label="Currency"
        [value]="displayCurrency | uppercase"
      ></app-details-item>
    }

    @if (financialAccount?.verificationStatus) {
      <app-details-item
        class="fa-details-verification-status"
        [showPlaceholder]="isLoading"
        label="Verification Status"
        [contentValue]="true"
      >
        <div class="verification-status">
          @if (isVerifiedByFlinks) {
            <div class="flinks-verification-btn">
              <div class="verified-text">Verified by</div>
              <rkt-icon class="rkt-icon" name="flinks-icon"></rkt-icon>
            </div>
          }
          @if (!isVerifiedByFlinks) {
            <div [class]="'inline-status inline-status-' + verificationStatus | lowercase">
              {{ verificationStatus | titlecase }}
            </div>
          }
          @if (isEligibleForVerification) {
            <button
              rktButton
              title="Request Account Verification"
              [disabled]="!!(isLoading | accessControl | async)"
              class="add-entity-btn rkt-button-link"
              (click)="onRequestAccountVerification()"
            >
              <div class="rkt-icon-link-text">
                <rkt-icon class="rkt-icon" name="bank"></rkt-icon>
                <div class="rkt-icon-text">Request Account Verification</div>
              </div>
            </button>
          }
        </div>
      </app-details-item>
    }

    @if (financialAccount?.verificationMethod) {
      <app-details-item
        class="fa-details-verification-method"
        [showPlaceholder]="isLoading"
        label="Verification Method"
        [value]="$safeNavigationMigration(financialAccount?.verificationMethod) | titlecase"
      ></app-details-item>
    }

    @if (financialAccount?.token) {
      <app-details-item
        class="fa-token"
        [showPlaceholder]="isLoading"
        label="Token"
        [value]="$safeNavigationMigration(financialAccount?.token) | titlecase"
      ></app-details-item>
    }

    @if (financialAccount?.provider) {
      <app-details-item
        class="fa-provider"
        [showPlaceholder]="isLoading"
        label="Provider"
        [value]="$safeNavigationMigration(financialAccount?.provider) | titlecase"
      ></app-details-item>
    }
  </div>

  @if (isEscrowAccount) {
    <div class="details-separator"></div>
  }

  @if (isLoading) {
    <div class="financial-account-fields">
      <div class="financial-account-fields-type">
        <app-text-with-placeholder [showPlaceholder]="true" [darkBg]="!isEscrowAccount"></app-text-with-placeholder>
      </div>
      <div class="details-row">
        <app-details-item
          class="fa-details-bank-name"
          [showPlaceholder]="true"
          label=""
          [value]=""
          [isDarkBg]="!isEscrowAccount"
        ></app-details-item>
        <app-details-item
          class="fa-details-name-on-account"
          [showPlaceholder]="true"
          label=""
          [value]=""
          [isDarkBg]="!isEscrowAccount"
        ></app-details-item>
        <app-details-item
          class="fa-details-account-number"
          [showPlaceholder]="true"
          label=""
          [value]=""
          [isDarkBg]="!isEscrowAccount"
        ></app-details-item>
      </div>
    </div>
  }

  @if (financialAccount?.bankAccount) {
    <div [class.financial-account-fields]="!isEscrowAccount">
      <div class="financial-account-fields-type">
        @if (!isCryptoAccount) {
          <rkt-icon name="bank"></rkt-icon>
        }
        {{ financialAccount?.type }}
      </div>
      <div class="details-row">
        <app-details-item
          class="fa-details-name-on-account"
          [showPlaceholder]="isLoading"
          label="Name on Account"
          [value]="$safeNavigationMigration(financialAccount?.bankAccount?.nameOnAccount)"
        ></app-details-item>
        @if (financialAccount?.bankAccount?.routingNo && !isCryptoAccount) {
          <app-details-item
            class="fa-details-routing-no"
            [showPlaceholder]="isLoading"
            label="Routing No."
            [value]="$safeNavigationMigration(financialAccount?.bankAccount?.routingNo)"
          ></app-details-item>
        }
        @if (!financialAccount?.maskedIban) {
          <app-details-item
            class="fa-details-account-number"
            [showPlaceholder]="isLoading"
            label="Account Number"
            [value]="unmaskedAccountNumber ?? accountNumber"
          ></app-details-item>
        }
        @if (!isCryptoAccount) {
          <app-details-item
            class="fa-details-bank-name"
            [showPlaceholder]="isLoading"
            label="Bank Name"
            [value]="$safeNavigationMigration(financialAccount?.bankAccount?.bankName)"
          ></app-details-item>
          @if (financialAccount?.maskedIban) {
            <app-details-item class="fa-details-iban" [showPlaceholder]="isLoading" label="IBAN" [value]="formattedIban"></app-details-item>
          }
          @if (financialAccount?.maskedIban) {
            <app-details-item
              class="fa-details-bic"
              [showPlaceholder]="isLoading"
              label="BIC"
              [value]="$safeNavigationMigration(financialAccount?.bankAccount?.bic)"
            ></app-details-item>
          }
          <app-details-item
            class="fa-details-account-number"
            [showPlaceholder]="isLoading"
            label="Address"
            [value]="$safeNavigationMigration(financialAccount?.bankAccount?.billingAddress?.addressLine1)"
          >
            @if (financialAccount?.bankAccount?.billingAddress?.addressLine1) {
              <div>{{ financialAccount?.bankAccount?.billingAddress?.addressLine2 }}</div>
              <div>
                {{ financialAccount?.bankAccount?.billingAddress?.city }}, {{ financialAccount?.bankAccount?.billingAddress?.state }}
              </div>
              <div>
                {{ financialAccount?.bankAccount?.billingAddress?.postalCode }} {{ financialAccount?.bankAccount?.billingAddress?.country }}
              </div>
            }
          </app-details-item>
        }
      </div>
    </div>
  }

  @if (financialAccount?.card) {
    <div class="financial-account-fields">
      <div class="financial-account-fields-type">
        <rkt-icon name="card"></rkt-icon>
        {{ financialAccount?.type }}
      </div>
      <div class="details-row details-row-with-offset">
        <app-details-item
          class="fa-details-name-on-account"
          [showPlaceholder]="isLoading"
          label="Name on Card"
          [value]="$safeNavigationMigration(financialAccount?.card?.fullNameOnCard)"
        ></app-details-item>
        <app-details-item
          class="fa-details-account-number"
          [showPlaceholder]="isLoading"
          label="Card Number"
          [value]="unmaskedAccountNumber ?? '**** ' + financialAccount?.card?.cardNumberTail"
        ></app-details-item>
        <app-details-item
          class="fa-details-account-number"
          [showPlaceholder]="isLoading"
          label="Card Exp. Date"
          [value]="$safeNavigationMigration(financialAccount?.card?.expiry)"
        ></app-details-item>
      </div>
      <div class="details-row">
        <app-details-item
          class="fa-details-account-number"
          [showPlaceholder]="isLoading"
          label="Address"
          [value]="$safeNavigationMigration(financialAccount?.card?.billingAddress?.addressLine1)"
        >
          @if (financialAccount?.card?.billingAddress?.addressLine1) {
            <div>{{ financialAccount?.card?.billingAddress?.addressLine2 }}</div>
            <div>{{ financialAccount?.card?.billingAddress?.city }}, {{ financialAccount?.card?.billingAddress?.state }}</div>
            <div>{{ financialAccount?.card?.billingAddress?.postalCode }} {{ financialAccount?.card?.billingAddress?.country }}</div>
          }
        </app-details-item>
      </div>
    </div>
  }

  @if (isEscrowAccount) {
    <div class="details-separator"></div>
  }

  <div class="details-row details-row-with-offset">
    <app-details-item
      class="fa-details-created-at"
      [showPlaceholder]="isLoading"
      label="Created At"
      [value]="$safeNavigationMigration(financialAccount?.createdAt) | dateFormat"
    ></app-details-item>

    <app-details-item
      class="fa-details-created-by"
      [showPlaceholder]="isLoading"
      label="Created By"
      [value]="$safeNavigationMigration(financialAccount?.createdBy?.id) | teamMemberName | async"
    >
    </app-details-item>
  </div>

  <div class="details-row">
    <app-details-item
      class="fa-details-updated-at"
      [showPlaceholder]="isLoading"
      label="Updated At"
      [value]="$safeNavigationMigration(financialAccount?.updatedAt) | dateFormat"
    ></app-details-item>

    <app-details-item
      class="fa-details-updated-by"
      [showPlaceholder]="isLoading"
      label="Updated By"
      [value]="$safeNavigationMigration(financialAccount?.updatedBy?.id) | teamMemberName | async"
    ></app-details-item>
  </div>

  @if (financialAccountId) {
    <app-details-notes entityType="FINANCIAL_ACCOUNT" [entityId]="financialAccountId"></app-details-notes>
  }
</div>

@if (isIntegrated && isBankAccount && financialAccountId) {
  <app-financial-account-accepted-documents [financialAccountId]="financialAccountId"></app-financial-account-accepted-documents>
}
